import React, { useEffect, useMemo, useState } from 'react';
import SearchableSelector from './SearchableSelector';
import ContextualHelpPanel from './ContextualHelpPanel';

const buildTemplateItems = (templates = []) =>
  templates.map((template) => ({
    id: template.id,
    label: template.name,
    description: `${template.category || 'other'} | ${template.sections?.length || 0} sections`,
    searchText: [
      template.description,
      ...(template.sections || []).map((section) => section.title),
      ...(template.sections || []).flatMap((section) => (section.fields || []).map((field) => field.label)),
    ]
      .filter(Boolean)
      .join(' '),
  }));

const buildClientItems = (clients = []) =>
  clients.map((client) => ({
    id: client.id,
    label: client.name,
    description:
      [client.location, client.contactName, client.contactEmail].filter(Boolean).join(' | ') ||
      'No additional details',
    searchText: [client.notes, client.contactPhone].filter(Boolean).join(' '),
  }));

const NewFormForm = ({
  templates,
  clients,
  preselectedClient,
  preselectedTemplate,
  onCreate,
  onCancel,
  controls,
  onControlsChange,
  helpConfig,
}) => {
  const [selectedTemplateId, setSelectedTemplateId] = useState(preselectedTemplate?.id || '');
  const [selectedClientId, setSelectedClientId] = useState(preselectedClient?.id || '');

  useEffect(() => {
    setSelectedTemplateId(preselectedTemplate?.id || '');
  }, [preselectedTemplate?.id]);

  useEffect(() => {
    setSelectedClientId(preselectedClient?.id || '');
  }, [preselectedClient?.id]);

  const templateItems = useMemo(() => buildTemplateItems(templates), [templates]);
  const clientItems = useMemo(() => buildClientItems(clients), [clients]);
  const selectedTemplate = templates.find((template) => template.id === selectedTemplateId);
  const selectedClient = clients.find((client) => client.id === selectedClientId);

  const handleSubmit = (event) => {
    event.preventDefault();

    if (selectedTemplateId && selectedClientId) {
      onCreate(selectedTemplateId, selectedClientId);
    }
  };

  return (
    <div>
      {helpConfig && <ContextualHelpPanel {...helpConfig} />}

      <div
        style={{
          marginBottom: '30px',
          padding: '20px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
        }}
      >
        <h3 style={{ marginBottom: '15px', color: 'var(--primary)' }}>Create a New Form</h3>
        <p style={{ color: '#666', marginBottom: '15px' }}>
          Search through templates and clients to build a new form instance faster.
        </p>
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
          <div
            style={{
              flex: 1,
              minWidth: '200px',
              padding: '15px',
              backgroundColor: selectedTemplate ? '#e0f7fa' : 'white',
              border: selectedTemplate ? '2px solid var(--accent)' : '2px dashed #ccc',
              borderRadius: '8px',
              textAlign: 'center',
            }}
          >
            <div style={{ fontWeight: 600 }}>Step 1: Template</div>
            <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '5px' }}>
              {selectedTemplate ? selectedTemplate.name : 'Select a template'}
            </div>
          </div>
          <div
            style={{
              flex: 1,
              minWidth: '200px',
              padding: '15px',
              backgroundColor: selectedClient ? '#e0f7fa' : 'white',
              border: selectedClient ? '2px solid var(--accent)' : '2px dashed #ccc',
              borderRadius: '8px',
              textAlign: 'center',
            }}
          >
            <div style={{ fontWeight: 600 }}>Step 2: Client</div>
            <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '5px' }}>
              {selectedClient ? selectedClient.name : 'Select a client'}
            </div>
          </div>
          <div
            style={{
              flex: 1,
              minWidth: '200px',
              padding: '15px',
              backgroundColor: selectedTemplate && selectedClient ? '#d4edda' : 'white',
              border: selectedTemplate && selectedClient ? '2px solid #28a745' : '2px dashed #ccc',
              borderRadius: '8px',
              textAlign: 'center',
            }}
          >
            <div style={{ fontWeight: 600 }}>Step 3: Create</div>
            <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '5px' }}>
              {selectedTemplate && selectedClient ? 'Ready to create' : 'Complete steps 1 and 2'}
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid-2">
          <SearchableSelector
            id="template-selector"
            label="1. Select Template"
            items={templateItems}
            selectedId={selectedTemplateId}
            onSelect={setSelectedTemplateId}
            searchValue={controls.templateSearch}
            onSearchChange={(templateSearch) => onControlsChange({ templateSearch })}
            searchPlaceholder="Search templates by name, category, section, or field"
            selectionPlaceholder="No template selected yet."
            emptyMessage="No templates match the current search."
            required
            disabled={templates.length === 0}
          />

          <SearchableSelector
            id="client-selector"
            label="2. Select Client"
            items={clientItems}
            selectedId={selectedClientId}
            onSelect={setSelectedClientId}
            searchValue={controls.clientSearch}
            onSearchChange={(clientSearch) => onControlsChange({ clientSearch })}
            searchPlaceholder="Search clients by name, location, contact, or notes"
            selectionPlaceholder="No client selected yet."
            emptyMessage="No clients match the current search."
            required
            disabled={clients.length === 0}
          />
        </div>

        {(selectedTemplate || selectedClient) && (
          <div className="quote-box" style={{ marginTop: '20px', marginBottom: '20px' }}>
            <strong>Your Selection:</strong>
            <div style={{ marginTop: '10px' }}>
              {selectedTemplate && (
                <div>
                  <strong>Template:</strong> {selectedTemplate.name}
                </div>
              )}
              {selectedClient && (
                <div style={{ marginTop: '5px' }}>
                  <strong>Client:</strong> {selectedClient.name}
                </div>
              )}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', marginTop: '20px', flexWrap: 'wrap' }}>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!selectedTemplateId || !selectedClientId}
            style={{
              opacity: !selectedTemplateId || !selectedClientId ? 0.5 : 1,
              cursor: !selectedTemplateId || !selectedClientId ? 'not-allowed' : 'pointer',
            }}
          >
            {selectedTemplateId && selectedClientId ? 'Create Form' : 'Select Template and Client'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewFormForm;
